import {useFormatCurrency, useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {FC, useMemo} from 'react';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {SPACING} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Price, PriceV2} from '../../../types/Price';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Text} from '../../atoms/Text/Text';
import {useLowestProductPriceStateManager} from '../LowestProductPrice30D/useLowestProductPriceStateManager';

export interface FinalPriceProps {
  price: Price | PriceV2;
  isSizeSelected?: boolean;
  className?: string;
  'data-testid'?: string;
  hideFromLabel?: boolean;
  hideVatLabel?: boolean;
  showVatLabelInColumn?: boolean;
  align?: 'left' | 'right';
  lowestPrice30DProductStateManagerKey?: string;
  hideSaleHighlight?: boolean;
}

const FinalPriceLegacy: FC<FinalPriceProps & {price: Price}> = ({
  price,
  isSizeSelected,
  className,
  'data-testid': testId,
  hideFromLabel,
  hideSaleHighlight,
}) => {
  const {t} = useTranslation();
  const formatCurrency = useFormatCurrency();
  const hasReductions = Boolean(price.reductions?.length) && !hideSaleHighlight;

  const formattedCurrency = useMemo(
    () => formatCurrency(price.price.amount, price.price.currencyCode),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formatCurrency is a stable function
    [price.price.amount, price.price.currencyCode],
  );
  const hasFromPrice =
    price.hasDifferentPrices && !isSizeSelected && !hideFromLabel;

  return (
    <PriceContainer
      data-has-secondary-price-label={Boolean(price.secondaryPriceLabel)}
    >
      <StyledText
        className={className}
        $hasSale={hasReductions}
        $foregroundColor={price.priceForegroundColor?.hex}
        $backgroundColor={price.priceBackgroundColor?.hex}
        data-testid={hasFromPrice ? testId : undefined}
      >
        {hasFromPrice ? (
          <PriceWrapper
            dangerouslySetInnerHTML={{
              __html: wrapNonSpanText(
                t('from {price}', {
                  price: `<span>${formattedCurrency}</span>`,
                }),
              ),
            }}
            data-testid="from-price"
          />
        ) : (
          <PriceWrapper data-testid={!hasFromPrice ? testId : undefined}>
            {formattedCurrency}
          </PriceWrapper>
        )}
      </StyledText>
      {price.secondaryPriceLabel && (
        <SecondaryPriceLabel
          data-testid="secondaryPriceLabel"
          className={className}
        >
          {price.secondaryPriceLabel}
        </SecondaryPriceLabel>
      )}
    </PriceContainer>
  );
};

const FinalPriceNew: FC<FinalPriceProps & {price: PriceV2}> = ({
  price,
  className,
  showVatLabelInColumn,
  hideVatLabel,
  align = 'left',
  lowestPrice30DProductStateManagerKey,
}) => {
  const {open: openLowestProductPricePopup} = useLowestProductPriceStateManager(
    lowestPrice30DProductStateManagerKey,
  );

  return (
    <PriceContainer
      data-has-secondary-price-label={Boolean(
        price.finalPrice.secondaryPriceLabel,
      )}
    >
      <ItemWrapper>
        <StyledText
          className={className}
          $backgroundColor={price.finalPrice.priceBackgroundColor?.hex}
          data-inline-vat={showVatLabelInColumn}
          data-has-secondary-price-label={Boolean(
            price.finalPrice.secondaryPriceLabel,
          )}
          data-align={align}
        >
          <PriceAndVatWrapper>
            <PriceAndLowestPriceIconWrapper>
              {price?.lpl30d?.legalInfo && align === 'right' && (
                <InfoIcon
                  asset={GenericIconName.PriceInfo}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    openLowestProductPricePopup();
                  }}
                />
              )}

              <WrapperWithSecondaryPriceLabel>
                <PriceWrapper
                  data-testid="finalPrice"
                  dangerouslySetInnerHTML={{
                    __html: price.finalPrice.priceLabel.text,
                  }}
                />

                {price.finalPrice.secondaryPriceLabel && (
                  <SecondaryPriceLabel
                    data-testid="secondaryPriceLabel"
                    className={className}
                    dangerouslySetInnerHTML={{
                      __html: price.finalPrice.secondaryPriceLabel.text,
                    }}
                  />
                )}
              </WrapperWithSecondaryPriceLabel>

              {price?.lpl30d?.legalInfo && align === 'left' && (
                <InfoIcon
                  asset={GenericIconName.PriceInfo}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    openLowestProductPricePopup();
                  }}
                />
              )}
            </PriceAndLowestPriceIconWrapper>
            {price.finalPrice.vatLabel && !hideVatLabel && (
              <FinalPriceVatWrapper
                data-vat-label={showVatLabelInColumn}
                dangerouslySetInnerHTML={{
                  __html: price.finalPrice.vatLabel.text,
                }}
              />
            )}
          </PriceAndVatWrapper>
        </StyledText>
      </ItemWrapper>
    </PriceContainer>
  );
};

export const FinalPrice: FC<FinalPriceProps> = ({
  price,
  showVatLabelInColumn,
  align = 'left',
  hideVatLabel,
  'data-testid': testId = 'finalPrice',
  className,
  lowestPrice30DProductStateManagerKey,
  hideSaleHighlight,
}) => {
  if ('finalPrice' in price) {
    return (
      <FinalPriceNew
        price={price}
        showVatLabelInColumn={showVatLabelInColumn}
        hideVatLabel={hideVatLabel}
        align={align}
        className={className}
        lowestPrice30DProductStateManagerKey={
          lowestPrice30DProductStateManagerKey
        }
      />
    );
  }

  return (
    <FinalPriceLegacy
      price={price}
      data-testid={testId}
      className={className}
      hideSaleHighlight={hideSaleHighlight}
    />
  );
};

const ItemWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  &[data-has-secondary-price-label='true'] {
    flex-direction: row;
  }
`;

const SecondaryPriceLabel = styled.span`
  color: ${COLORS['Text.Grey']};
  white-space: nowrap;
`;

const PriceWrapper = styled.span`
  white-space: nowrap;
`;

const SALE_COLOR = COLORS.RedSale;

const WrapperWithSecondaryPriceLabel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

export const FinalPriceVatWrapper = styled.span`
  color: ${COLORS['Text.Grey']};
  font-size: ${FONT_SIZES.px9};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 11px;
  margin-block: ${SPACING['X.Xs']};
`;

const StyledText = styled(Text)<{
  $hasSale?: boolean;
  $foregroundColor?: string;
  $backgroundColor?: string;
}>`
  display: flex;

  &[data-has-secondary-price-label='true'] {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  &[data-align='right'][data-has-secondary-price-label='true'] {
    align-items: flex-end;
  }
  align-items: center;
  justify-content: center;

  gap: 4px;
  flex-wrap: wrap;
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: ${FONT_SIZES.px24};
  color: ${({$foregroundColor, $hasSale}) => {
    if ($foregroundColor) {
      return $foregroundColor;
    }

    return $hasSale ? SALE_COLOR : COLORS.Black;
  }};

  margin: ${props => (props.$backgroundColor ? '4px 0 2px 0' : '0')};

  ${PriceWrapper} {
    padding: ${props => (props.$backgroundColor ? '0 4px 1px 4px' : '0')};
    border-radius: ${props => (props.$backgroundColor ? '4px' : 'unset')};
    background-color: ${props =>
      props.$backgroundColor ? props.$backgroundColor : ''};
  }

  small {
    font-size: ${FONT_SIZES.px12};
  }

  &[data-inline-vat='true'] {
    flex-direction: column;
    line-height: 12px;
    align-items: end;
  }

  span[data-vat-label='true'] span {
    font-size: ${FONT_SIZES.px9};
    font-weight: ${FONT_WEIGHTS.mediumBold};
    line-height: 11px;
  }
`;

const InfoIcon = styled(Icon)`
  cursor: pointer;

  --size: 18px;
  width: var(--size);
  height: var(--size);
  flex-basis: var(--size);
`;

const PriceAndLowestPriceIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PriceAndVatWrapper = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;

  ${tabletAndUpLinaria(`
    align-items: center;
    flex-direction: row;
    gap: 4px;
  `)}
`;

/**
 * Wraps non-span text with the `<small>` tag. Used for the "from" text in the price that visually needs to be smaller.
 * The price text will be wrapped in a span tag, so this function wraps everything else, as the "from" text can appear before or after the price.
 */
function wrapNonSpanText(text: string): string {
  return text.replace(/(.*?)<span>(.*?)<\/span>(.*)/g, (_, p1, p2, p3) => {
    let result = p2;

    if (p1) {
      result = `<small>${p1}</small>${result}`;
    }

    if (p3) {
      result = `${result}<small>${p3}</small>`;
    }

    return result;
  });
}
